/**
 * The digit boundary, with exactly one implementation.
 *
 *   Human surfaces  → Arabic-Indic ٠١٢٣٤٥٦٧٨٩, through the PINNED formatter.
 *   Machine surfaces → ASCII: `<time datetime>`, `aria-valuenow`, every
 *   `data-*` a script reads a number back out of, CSS custom properties,
 *   hidden input values, and every API payload.
 *
 * A displayed numeral is never parsed back into a number. Conversion happens
 * at the render site, in this module, and nowhere else.
 */

/**
 * The numbering system is pinned IN THE TAG. A bare `ar` or `ar-YE` resolves
 * its default numbering system through ICU data that differs across engines
 * and versions, so the same string renders Arabic-Indic in one browser and
 * Latin in another — a bug that never reproduces on the reviewer's machine.
 */
const ARABIC_INDIC = new Intl.NumberFormat('ar-u-nu-arab');

/**
 * The same pinned numbering system with **grouping off**.
 *
 * A count is a quantity and reads better in thousands («‏٤١٬٢٠٠ مشاهدة»); a
 * YEAR, a resolution rung and a pixel geometry are not quantities — they are
 * figures that name something, and a thousands separator inside one turns
 * «‏٢٠٢٣» into «‏٢٬٠٢٣» and «‏٢١٦٠» into «‏٢٬١٦٠», which is a different token
 * rather than a nicer one.
 */
const ARABIC_INDIC_PLAIN = new Intl.NumberFormat('ar-u-nu-arab', { useGrouping: false });

/**
 * A rating score — EXACTLY one decimal place.
 *
 * Fixed rather than free: a provider that reports `8` and one that reports
 * `8.4` are the same kind of claim, and rendering «‏٨» beside «‏٨٫٤» in one rail
 * reads as two different precisions rather than as two scores.
 */
const ARABIC_INDIC_SCORE = new Intl.NumberFormat('ar-u-nu-arab', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const ARABIC_INDIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'] as const;

/** Human surface: a count, a price integer, an episode number. Grouped. */
export function formatNumber(value: number): string {
  return ARABIC_INDIC.format(value);
}

/** Human surface: a year, a resolution rung, a pixel geometry. **Ungrouped.** */
export function formatFigure(value: number): string {
  return ARABIC_INDIC_PLAIN.format(value);
}

/** Human surface: a provider rating score. One decimal, always `<bdi>`-isolated
 *  at the render site because a rating sits beside a Latin source name. */
export function formatScore(value: number): string {
  return ARABIC_INDIC_SCORE.format(value);
}

/**
 * Re-spells the ASCII digits inside an already-composed HUMAN string.
 * Used only for text a library produced for a human surface (a relative date,
 * for one). Never call it on a machine surface — that is the auto-conversion
 * the boundary rule forbids.
 */
export function toArabicIndicDigits(text: string): string {
  return text.replace(/[0-9]/g, (digit) => ARABIC_INDIC_DIGITS[Number(digit)] ?? digit);
}

/**
 * Folds Arabic-Indic (and Eastern Arabic-Indic) digits to ASCII.
 * Input only: a phone field must accept ٠٧٧٠… and submit a machine-clean
 * value. Never reject a valid number because of the glyph the keyboard
 * produced.
 */
export function toAsciiDigits(text: string): string {
  return text.replace(/[٠-٩۰-۹]/g, (digit) => {
    const code = digit.charCodeAt(0);
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
    return String(code - base);
  });
}
